import {
  faCheck,
  faChevronDown,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { FC, useEffect, useRef, useState } from 'react'
import styles from './locationFilter.module.scss'

interface LocationFilterProps {
  locations: string[]
  onChange: (location: string | undefined) => void
  // undefined = All Locations
  value?: string
}

// "All Locations": the facility page's header filter, and each widget's own.
export const LocationFilter: FC<LocationFilterProps> = ({
  locations,
  onChange,
  value,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const pick = (location: string | undefined) => {
    onChange(location)
    setOpen(false)
  }

  const options: { label: string; value: string | undefined }[] = [
    { label: 'All Locations', value: undefined },
    ...locations.map((location) => ({ label: location, value: location })),
  ]

  return (
    <div className={styles.filter} ref={ref}>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        className={classNames(
          styles.button,
          (open || value) && styles.buttonActive
        )}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <FontAwesomeIcon className={styles.pin} icon={faMapMarkerAlt} />
        <span className={styles.label}>{value ?? 'All Locations'}</span>
        <FontAwesomeIcon
          className={classNames(styles.chevron, open && styles.chevronOpen)}
          icon={faChevronDown}
        />
      </button>
      {open && (
        <ul className={styles.menu} role="listbox">
          {options.map((option) => {
            const on = option.value === value
            return (
              <li aria-selected={on} key={option.label} role="option">
                <button
                  className={classNames(styles.option, on && styles.optionOn)}
                  onClick={() => pick(option.value)}
                  type="button"
                >
                  <span className={styles.label}>{option.label}</span>
                  {on && (
                    <FontAwesomeIcon className={styles.check} icon={faCheck} />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
